import React, { useState } from 'react'
import { useStripe } from '../hooks/useStripe'
import { SUBSCRIPTION_PLANS } from '../stripe/StripeConfig'
import { TEST_MODE } from '../config/testMode'

interface PricingPageProps {
  userId?: string
  currentPlan?: 'free' | 'monthly' | 'yearly'
}

const PricingPage: React.FC<PricingPageProps> = ({ userId, currentPlan = 'free' }) => {
  const { loading, createCheckoutSession, plans } = useStripe()
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly')
  const [showTrialInfo, setShowTrialInfo] = useState(false)

  const handleSubscribe = async (planId: 'monthly' | 'yearly') => {
    if (!userId) {
      // Redirect to login
      window.location.href = '/?signup=true'
      return
    }

    try {
      const result = await createCheckoutSession(planId, userId)
      if (result.url) {
        window.location.href = result.url
      }
    } catch (err) {
      console.error('Checkout error:', err)
    }
  }

  return (
    <div>
      {/* Hero */}
      <div className="card" style={{ 
        background: 'linear-gradient(135deg, #2d5a27, #4a7c43)', 
        color: 'white',
        textAlign: 'center',
        padding: '3rem 2rem'
      }}>
        <h1 style={{ margin: '0 0 1rem 0', fontSize: '2.5rem' }}>
          {TEST_MODE.DISABLE_PAYMENTS ? '🧪 Test Mode - All Features Free' : 'Choose Your Garden Plan'}
        </h1>
        <p style={{ margin: 0, fontSize: '1.2rem', opacity: 0.9 }}>
          {TEST_MODE.DISABLE_PAYMENTS 
            ? 'All premium features are unlocked for testing. No payment required.' 
            : "Start free, upgrade when you're ready. No credit card required."}
        </p>
      </div>
      
      {/* Test Mode Notice */}
      {TEST_MODE.DISABLE_PAYMENTS && (
        <div className="card" style={{ 
          background: '#fff3cd', 
          border: '2px solid #ff6b35',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#ff6b35', margin: '0 0 0.5rem 0' }}>🧪 Testing Mode Active</h3>
          <p style={{ margin: 0 }}>
            Stripe payments are disabled. All users have premium access automatically. 
            This mode is for development and testing only.
          </p>
        </div>
      )}

      {/* Billing Toggle */}
      <div className="card" style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
        <button
          className="btn"
          style={{
            background: billingPeriod === 'monthly' ? '#2d5a27' : 'white',
            color: billingPeriod === 'monthly' ? 'white' : '#2d5a27'
          }}
          onClick={() => setBillingPeriod('monthly')}
        >
          Monthly
        </button>
        <button
          className="btn"
          style={{
            background: billingPeriod === 'yearly' ? '#2d5a27' : 'white',
            color: billingPeriod === 'yearly' ? 'white' : '#2d5a27'
          }}
          onClick={() => setBillingPeriod('yearly')}
        >
          Yearly <span style={{ fontSize: '0.8rem', marginLeft: '0.5rem' }}>Save 17%</span>
        </button>
      </div>

      {/* Pricing Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem'
      }}>
        {plans.map(plan => {
          const isCurrentPlan = currentPlan === plan.id
          const isPremium = plan.id !== 'free'
          const displayPrice = plan.id === 'yearly' && billingPeriod === 'yearly' 
            ? plan.price 
            : plan.id === 'yearly' && billingPeriod === 'monthly'
            ? 29
            : plan.price

          return (
            <div 
              key={plan.id}
              className="card"
              style={{
                position: 'relative',
                border: plan.popular ? '3px solid #ffd700' : '1px solid #ddd',
                transform: plan.popular ? 'scale(1.02)' : 'none',
                boxShadow: plan.popular ? '0 8px 24px rgba(0,0,0,0.15)' : 'none'
              }}
            >
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#ffd700',
                  color: '#5a4a00',
                  padding: '0.25rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}>
                  ⭐ Most Popular
                </div>
              )}

              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0' }}>{plan.name}</h3>
                <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                  {plan.description}
                </p>
              </div>

              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '3rem', fontWeight: 'bold' }}>
                  ${displayPrice}
                </span>
                <span style={{ color: '#666' }}>/{plan.period}</span>
                {plan.savings && billingPeriod === 'yearly' && (
                  <div style={{ 
                    marginTop: '0.5rem', 
                    color: '#4caf50', 
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}>
                    Save ${plan.savings}/year
                  </div>
                )}
              </div>

              {plan.trialDays && (
                <div style={{
                  textAlign: 'center',
                  padding: '0.75rem',
                  background: '#e8f5e9',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  <strong>{plan.trialDays}-day free trial</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                    No credit card required
                  </p>
                </div>
              )}

              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: '0 0 1.5rem 0'
              }}>
                {plan.features.map((feature, i) => (
                  <li 
                    key={i}
                    style={{
                      padding: '0.5rem 0',
                      borderBottom: i < plan.features.length - 1 ? '1px solid #eee' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <span style={{ color: '#4caf50' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                className="btn"
                style={{
                  width: '100%',
                  background: isCurrentPlan ? '#e0e0e0' : plan.popular ? '#2d5a27' : 'white',
                  color: isCurrentPlan ? '#666' : plan.popular ? 'white' : '#2d5a27',
                  border: isCurrentPlan ? 'none' : plan.popular ? 'none' : '2px solid #2d5a27',
                  cursor: isCurrentPlan ? 'default' : 'pointer'
                }}
                onClick={() => !isCurrentPlan && plan.id !== 'free' && handleSubscribe(plan.id as 'monthly' | 'yearly')}
                disabled={isCurrentPlan || loading}
              >
                {isCurrentPlan ? 'Current Plan' : loading ? 'Loading...' : plan.cta}
              </button>
            </div>
          )
        })}
      </div>

      {/* FAQ */}
      <div className="card">
        <h3 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Frequently Asked Questions</h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 500, padding: '0.5rem 0' }}>
              Can I cancel anytime?
            </summary>
            <p style={{ margin: '0.5rem 0 0 0', paddingLeft: '1rem', color: '#666' }}>
              Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
            </p>
          </details>
          
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 500, padding: '0.5rem 0' }}>
              What happens after my free trial?
            </summary>
            <p style={{ margin: '0.5rem 0 0 0', paddingLeft: '1rem', color: '#666' }}>
              After your 14-day trial, you'll be charged the monthly or annual fee depending on your chosen plan. We'll send you a reminder 3 days before your trial ends.
            </p>
          </details>
          
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 500, padding: '0.5rem 0' }}>
              Can I switch between monthly and yearly?
            </summary>
            <p style={{ margin: '0.5rem 0 0 0', paddingLeft: '1rem', color: '#666' }}>
              Yes, you can switch at any time. If you switch to yearly, you'll be charged the prorated difference. If you switch to monthly, the change takes effect at your next billing date.
            </p>
          </details>
          
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 500, padding: '0.5rem 0' }}>
              Is my data secure?
            </summary>
            <p style={{ margin: '0.5rem 0 0 0', paddingLeft: '1rem', color: '#666' }}>
              Absolutely. We use industry-standard encryption and never share your personal information. Your garden data belongs to you.
            </p>
          </details>
          
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 500, padding: '0.5rem 0' }}>
              Do you offer refunds?
            </summary>
            <p style={{ margin: '0.5rem 0 0 0', paddingLeft: '1rem', color: '#666' }}>
              If you're not satisfied, contact us within 30 days for a full refund. No questions asked.
            </p>
          </details>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="card" style={{ textAlign: 'center' }}>
        <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
          Trusted by 150+ gardeners across the country
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>🔒</span>
            <span>SSL Secure</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>💳</span>
            <span>Stripe Payments</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>✓</span>
            <span>30-Day Guarantee</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingPage
